import React, { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Film, Plus, Trash2, Loader2, Download, Play, Layers } from "lucide-react";

const SIZES = [
  { key: "1280x720", label: "16:9 HD" },
  { key: "1024x1792", label: "9:16 Portrait" },
  { key: "1024x1024", label: "1:1 Carré" },
  { key: "1792x1024", label: "Ultra Wide" },
];
const DURATIONS = [4, 8, 12];
const TRANSITIONS = [
  { key: "fade", label: "Fondu" },
  { key: "fadeblack", label: "Fondu noir" },
  { key: "fadewhite", label: "Fondu blanc" },
  { key: "slideleft", label: "Slide gauche" },
  { key: "wipeleft", label: "Wipe" },
  { key: "cut", label: "Coupe nette" },
];

const newScene = () => ({ id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, prompt: "", duration: 4, size: "1280x720" });

export default function VideoSequenceStudio() {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState([newScene(), newScene()]);
  const [model, setModel] = useState("sora-2");
  const [transition, setTransition] = useState("fade");
  const [xdur, setXdur] = useState(0.6);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progressSec, setProgressSec] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startPolling = (projectId) => {
    timerRef.current = setInterval(() => setProgressSec(s => s + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        if (data.status === "completed" && data.data_url) {
          stopPolling();
          setResult(data); setLoading(false);
          toast.success("Séquence assemblée !");
        } else if (data.status === "failed") {
          stopPolling(); setLoading(false);
          toast.error(data.error || "Génération échouée");
        }
      } catch (e) { console.warn("poll error:", e?.message); }
    }, 10000);
  };

  const updateScene = (i, patch) => setScenes(scenes.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeScene = (i) => setScenes(scenes.filter((_, idx) => idx !== i));
  const addScene = () => { if (scenes.length < 5) setScenes([...scenes, newScene()]); };

  const generate = async () => {
    if (scenes.some(s => !s.prompt.trim())) { toast.error("Chaque scène doit avoir un prompt"); return; }
    stopPolling();
    setLoading(true); setResult(null); setProgressSec(0);
    try {
      const { data } = await api.post("/ad/sequence", {
        title: title || undefined,
        scenes,
        model,
        transition,
        transition_duration: xdur,
      });
      if (!data?.project_id) throw new Error("Pas de project_id renvoyé");
      toast.info(`Sora génère ${scenes.length} scènes en parallèle...`);
      startPolling(data.project_id);
    } catch (e) {
      setLoading(false);
      toast.error(e.response?.data?.detail || "Erreur démarrage séquence");
    }
  };

  const download = () => {
    if (!result?.data_url) return;
    const a = document.createElement("a");
    a.href = result.data_url; a.download = `xookpub-sequence-${Date.now()}.mp4`; a.click();
  };

  const totalDur = scenes.reduce((acc, s) => acc + s.duration, 0);
  const estMin = Math.ceil(scenes.length * 3); // ~3 min par scène Sora

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-mono">Studio · Multi-scènes Sora</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">
            <span className="gold-text">Séquences vidéo</span> assemblées par l'IA
          </h1>
          <p className="mt-2 text-white/55 max-w-2xl">Décrivez 2 à 5 plans, Sora 2 les génère en parallèle, ffmpeg les fusionne avec transitions.</p>
        </div>
        <Layers className="w-10 h-10 text-gold" />
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          {scenes.map((s, i) => (
            <div key={s.id} className="studio-card p-5" data-testid={`scene-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center text-sm font-semibold">{i+1}</div>
                  <span className="font-heading">Scène {i+1}</span>
                </div>
                {scenes.length > 1 && (
                  <button data-testid={`remove-scene-${i}`} onClick={()=>removeScene(i)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <textarea
                data-testid={`scene-prompt-${i}`}
                value={s.prompt}
                onChange={(e)=>updateScene(i, {prompt: e.target.value})}
                placeholder={`Plan ${i+1} : décrivez la scène, l'ambiance, le mouvement de caméra...`}
                className="w-full h-20 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none"
              />
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <div className="flex gap-1">
                  {DURATIONS.map(d => (
                    <button key={d} data-testid={`scene-${i}-dur-${d}`} onClick={()=>updateScene(i, {duration: d})}
                      className={`px-3 py-1.5 rounded text-xs ${s.duration===d ? "bg-gold text-black" : "bg-white/5 text-white/70"}`}>{d}s</button>
                  ))}
                </div>
                <select data-testid={`scene-${i}-size`} value={s.size} onChange={(e)=>updateScene(i, {size: e.target.value})}
                  className="bg-obsidian-light border border-white/10 rounded px-2 py-1.5 text-xs">
                  {SIZES.map(sz => <option key={sz.key} value={sz.key}>{sz.label}</option>)}
                </select>
              </div>
            </div>
          ))}

          {scenes.length < 5 && (
            <button data-testid="add-scene" onClick={addScene} className="ghost-btn w-full !py-3 border-dashed">
              <Plus className="w-4 h-4" /> Ajouter une scène
            </button>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="studio-card p-5 sticky top-24 space-y-5">
            <div>
              <label className="label-mono">Titre</label>
              <input data-testid="seq-title" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ex: Pub Hiver 2026"
                className="mt-2 w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50" />
            </div>

            <div>
              <label className="label-mono">Modèle Sora</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button data-testid="seq-model-sora-2" onClick={()=>setModel("sora-2")}
                  className={`py-2 rounded text-sm ${model==="sora-2"?"bg-gold text-black":"bg-white/5"}`}>Sora 2</button>
                <button data-testid="seq-model-pro" onClick={()=>setModel("sora-2-pro")}
                  className={`py-2 rounded text-sm ${model==="sora-2-pro"?"bg-gold text-black":"bg-white/5"}`}>Sora 2 Pro</button>
              </div>
            </div>

            <div>
              <label className="label-mono">Transition</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {TRANSITIONS.map(t => (
                  <button key={t.key} data-testid={`trans-${t.key}`} onClick={()=>setTransition(t.key)}
                    className={`py-2 px-3 rounded text-xs ${transition===t.key ? "bg-gold text-black" : "bg-white/5 text-white/70"}`}>{t.label}</button>
                ))}
              </div>
              {transition !== "cut" && (
                <div className="mt-3">
                  <label className="text-xs text-white/60">Durée transition : {xdur.toFixed(1)}s</label>
                  <input data-testid="trans-duration" type="range" min="0.2" max="1.5" step="0.1" value={xdur}
                    onChange={e=>setXdur(+e.target.value)} className="w-full accent-[#D4AF37]" />
                </div>
              )}
            </div>

            <div className="rounded-lg bg-gold/5 border border-gold/20 p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-white/70">Scènes</span><span className="font-mono text-gold">{scenes.length}</span></div>
              <div className="flex justify-between"><span className="text-white/70">Durée totale</span><span className="font-mono text-gold">~{totalDur}s</span></div>
              <div className="flex justify-between"><span className="text-white/70">Temps estimé</span><span className="font-mono text-gold">~{estMin} min</span></div>
            </div>

            <div className="aspect-video bg-obsidian rounded-xl border border-white/5 flex items-center justify-center overflow-hidden">
              {loading ? (
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto rounded-full border-2 border-gold border-t-transparent animate-spin" />
                  <div className="mt-3 label-mono">Sora génère {scenes.length} scènes...</div>
                  <div className="text-xs text-white/40 mt-1" data-testid="seq-elapsed">
                    {Math.floor(progressSec / 60)}min {progressSec % 60}s écoulés
                  </div>
                  <div className="text-[11px] text-white/30 mt-1 max-w-xs mx-auto">Vous pouvez naviguer, ça continue en arrière-plan.</div>
                </div>
              ) : result?.data_url ? (
                <video src={result.data_url} controls className="w-full h-full" data-testid="sequence-result" />
              ) : (
                <div className="text-center text-white/30"><Play className="w-8 h-8 mx-auto" /></div>
              )}
            </div>

            <div className="flex gap-2">
              <button data-testid="seq-generate" onClick={generate} disabled={loading} className="gold-btn flex-1 !py-3 disabled:opacity-50">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération...</> : <><Film className="w-4 h-4" /> Assembler la séquence</>}
              </button>
              {result?.data_url && (
                <button data-testid="seq-download" onClick={download} className="ghost-btn !py-3 !px-4">
                  <Download className="w-4 h-4" />
                </button>
              )}
            </div>

            <button onClick={()=>navigate("/studio/composer")} className="w-full text-sm text-gold/80 hover:text-gold text-center pt-2 border-t border-white/5">
              Ajouter voix off + musique → Composer la pub finale →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
